import { useMemo } from 'react';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { Namespaces } from 'types/locales';
import { STATIC_SQUARE_FOOTAGE_STATS } from 'utils/static-square-footage-stats';
import { formatRatio } from 'utils/format-ratio';
import type { InsightsResponse } from '@zoocasa/go-search';
import type { InsightsListingType } from 'data/insights/types';

/** id of the MarketFaqAccordion section this bar's "More Stats" link scrolls to. */
const FAQ_ANCHOR_ID = 'market-faq';

const EN_DEFAULTS: Record<string, string> = {
  'hybridPage.listingStatsBar.avgListingPriceLabel': 'The Avg. Listing Price Is',
  'hybridPage.listingStatsBar.avgPricePerSqftLabel': 'The Avg. Price Per Sqft Is',
  'hybridPage.listingStatsBar.avgDaysOnMarketLabel': 'The Avg Days On Market Is',
  'hybridPage.listingStatsBar.sellToListRatioLabel': 'The Sell-To-List Ratio Is',
  'hybridPage.listingStatsBar.moreStatsLabel': 'More Stats',
  // Full-width (province/country) cards are wider than the default layout's — "View More Stats"
  // keeps this card's width visually consistent with the others instead of looking undersized.
  'hybridPage.listingStatsBar.moreStatsLabelFullWidth': 'View More Stats',
  'hybridPage.listingStatsBar.perSqftValue': '{{value}}/sqft',
  'hybridPage.listingStatsBar.daysValue': '{{days}} Days',
  'hybridPage.listingStatsBar.avgAskingRentLabel': 'The Avg. Asking Rent Is',
  'hybridPage.listingStatsBar.avgSoldPriceLabel': 'The Avg. Sold Price Is',
  'hybridPage.listingStatsBar.avgDaysToLeaseLabel': 'The Avg Days To Lease Is',
  'hybridPage.listingStatsBar.leaseToListRatioLabel': 'The Lease-To-List Ratio Is',
  'hybridPage.listingStatsBar.perMonthValue': '{{value}}/mo',
};

/** Label keys per market for the price, days on market and list ratio cards. */
const LABEL_KEYS: Record<InsightsListingType, { price: string; dom: string; ratio: string }> = {
  sale: {
    price: 'hybridPage.listingStatsBar.avgListingPriceLabel',
    dom: 'hybridPage.listingStatsBar.avgDaysOnMarketLabel',
    ratio: 'hybridPage.listingStatsBar.sellToListRatioLabel',
  },
  rent: {
    price: 'hybridPage.listingStatsBar.avgAskingRentLabel',
    dom: 'hybridPage.listingStatsBar.avgDaysToLeaseLabel',
    ratio: 'hybridPage.listingStatsBar.leaseToListRatioLabel',
  },
  sold: {
    price: 'hybridPage.listingStatsBar.avgSoldPriceLabel',
    dom: 'hybridPage.listingStatsBar.avgDaysOnMarketLabel',
    ratio: 'hybridPage.listingStatsBar.sellToListRatioLabel',
  },
};

export interface ListingStatsBarProps {
  /** Market insights for the current area (avg price, price/sqft, DOM, sell/list ratio). */
  insightsData?: InsightsResponse;
  /** Area's slug (e.g. "toronto-on") — used to fill in avg price/sqft for cities where the API reliably returns 0. */
  citySlug?: string;
  /** Called instead of the default window-scrolling anchor jump when "More Stats" is clicked. */
  onMoreStatsClick?: () => void;
  /**
   * Province/country pages hide the map and stretch `.area-page-content` full-width, so there's
   * no map gutter for the bar's usual negative-margin bleed to fill — it just overshoots past the
   * page's own left inset. Set this to align the bar with the page's normal side padding instead.
   */
  isFullWidth?: boolean;
  /** Optional class applied to the root element (e.g. area-page column spacing overrides). */
  className?: string;
  /**
   * Market `insightsData` describes; switches the labels. Rents show per month, and price per
   * sqft (a for-sale figure) is hidden for rent and sold. Defaults to homes for sale.
   */
  listingType?: InsightsListingType;
}

interface StatDescriptor {
  key: string;
  label: string;
  value: string;
  icon: JSX.Element;
  iconColor: string;
}

const Icons = {
  dollar: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2V22M17 5H9.5C8.57 5 7.68 5.37 7.02 6.02C6.37 6.68 6 7.57 6 8.5C6 9.43 6.37 10.32 7.02 10.98C7.68 11.63 8.57 12 9.5 12H14.5C15.43 12 16.32 12.37 16.98 13.02C17.63 13.68 18 14.57 18 15.5C18 16.43 17.63 17.32 16.98 17.98C16.32 18.63 15.43 19 14.5 19H6" stroke="#3b6bee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  grid: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="#2f9e5c" strokeWidth="2"/>
      <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="#2f9e5c" strokeWidth="2"/>
      <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="#2f9e5c" strokeWidth="2"/>
      <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="#2f9e5c" strokeWidth="2"/>
    </svg>
  ),
  calendar: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="#7c3aed" strokeWidth="2"/>
      <path d="M3 9H21" stroke="#7c3aed" strokeWidth="2"/>
      <path d="M8 3V6M16 3V6" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  home: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.55 5.45 21 6 21H9M19 10L21 12M19 10V20C19 20.55 18.55 21 18 21H15M9 21C9.55 21 10 20.55 10 20V16C10 15.45 10.45 15 11 15H13C13.55 15 14 15.45 14 16V20C14 20.55 14.45 21 15 21M9 21H15" stroke="#2f8fd1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  arrow: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

function formatCurrency(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

export default function ListingStatsBar({ insightsData, citySlug, onMoreStatsClick, isFullWidth, className, listingType = 'sale' }: ListingStatsBarProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const interpolate = (template: string, values?: Record<string, string>) =>
    values ? template.replace(/\{\{(\w+)\}\}/g, (_, k) => (k in values ? values[k] : `{{${k}}}`)) : template;
  const tr = (key: string, values?: Record<string, string>) =>
    interpolate(t(key, { defaultValue: EN_DEFAULTS[key] }), values);

  const stats = useMemo((): StatDescriptor[] => {
    if (!insightsData) return [];
    const labelKeys = LABEL_KEYS[listingType];
    // The search API reliably returns 0 for AvgPricePerSqft at the city level — fall back to
    // the static per-city table so this stat doesn't just disappear for the cities we have it for.
    // Both are for-sale figures, so rent and sold drop the card.
    const avgPricePerSqft = listingType === 'sale'
      ? (citySlug && STATIC_SQUARE_FOOTAGE_STATS[citySlug]?.avgPricePerSqft) || insightsData.AvgPricePerSqft
      : 0;
    const avgPrice = formatCurrency(insightsData.AvgTotalPrice);
    const all: (StatDescriptor & { value_: number })[] = [
      {
        key: 'avgPrice',
        label: tr(labelKeys.price),
        value_: insightsData.AvgTotalPrice,
        value: listingType === 'rent' ? tr('hybridPage.listingStatsBar.perMonthValue', { value: avgPrice }) : avgPrice,
        icon: Icons.dollar,
        iconColor: 'icon-blue',
      },
      {
        key: 'avgPpsf',
        label: tr('hybridPage.listingStatsBar.avgPricePerSqftLabel'),
        value_: avgPricePerSqft,
        value: tr('hybridPage.listingStatsBar.perSqftValue', { value: formatCurrency(avgPricePerSqft) }),
        icon: Icons.grid,
        iconColor: 'icon-green',
      },
      {
        key: 'dom',
        label: tr(labelKeys.dom),
        value_: insightsData.DaysOnMarket,
        value: tr('hybridPage.listingStatsBar.daysValue', { days: String(Math.round(insightsData.DaysOnMarket)) }),
        icon: Icons.calendar,
        iconColor: 'icon-purple',
      },
      {
        key: 'slr',
        label: tr(labelKeys.ratio),
        value_: insightsData.SellListRatio,
        value: formatRatio(insightsData.SellListRatio),
        icon: Icons.home,
        iconColor: 'icon-sky',
      },
    ];
    return all.filter(s => s.value_ > 0).map(({ value_, ...rest }) => rest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightsData, citySlug, t, listingType]);

  if (stats.length === 0) return null;

  return (
    <div className={buildClassName(styles['listing-stats-bar'], isFullWidth && styles['full-width'], className)}>
      <div className={styles['listing-stats-track']}>
        {stats.map(stat => (
          <div key={stat.key} className={styles['stat-card']}>
            <div className={buildClassName(styles['stat-icon'], styles[stat.iconColor])}>
              {stat.icon}
            </div>
            <div className={styles['stat-text']}>
              <div className={styles['stat-label']}>{stat.label}</div>
              <div className={styles['stat-value']}>{stat.value}</div>
            </div>
          </div>
        ))}
        <a
          href={`#${FAQ_ANCHOR_ID}`}
          className={styles['more-stats-card']}
          onClick={e => {
            e.preventDefault();
            if (onMoreStatsClick) {
              onMoreStatsClick();
            } else {
              document.getElementById(FAQ_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          <span className={styles['more-stats-label']}>
            {isFullWidth
              ? tr('hybridPage.listingStatsBar.moreStatsLabelFullWidth')
              : tr('hybridPage.listingStatsBar.moreStatsLabel')}
          </span>
          <span className={styles['more-stats-arrow']}>{Icons.arrow}</span>
        </a>
      </div>
    </div>
  );
}
